import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BillingPlanTier } from '@recruitee/products-types';

import { ready } from '../../../../helpers/rx-operators';
import {
  PricingFeatureLoadedModel,
  PricingFeatureObservableModel,
} from '../../../../types/pricing-feature.types';
import {
  AddonModelUI,
  FeatureComparisonUIData,
  FeatureModelUI,
  FeatureItemUI,
} from './feature-compare-ui.types';
import {
  FeatureRecord,
  GrowAddons,
  GrowFeatures,
  OptimizeAddons,
  OptimizeFeatures,
  StartAddons,
  StartFeatures,
} from './feature-compare.data';

@Injectable()
export class FeatureCompareLogicService {
  constructor(private featureModel: PricingFeatureObservableModel) {}

  public getData$(): Observable<FeatureComparisonUIData> {
    return this.featureModel.pipe(
      ready,
      map(model => ({
        features: this.getFeatureData(model),
        addons: this.getAddonData(),
        showStartDowngradeWarning: model.subscription?.tier === BillingPlanTier.Scale,
      })),
    );
  }

  private getFeatureData(model: PricingFeatureLoadedModel): FeatureModelUI {
    const tier = model.subscription?.tier;
    const isMigrating = tier && [BillingPlanTier.Launch, BillingPlanTier.Scale].includes(tier);
    return {
      [BillingPlanTier.Start]: [
        StartFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Start, isMigrating ? tier : undefined),
        ),
      ],
      [BillingPlanTier.Grow]: [
        StartFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Grow, isMigrating ? tier : undefined),
        ),
        GrowFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Grow, isMigrating ? tier : undefined),
        ),
      ],
      [BillingPlanTier.Optimize]: [
        StartFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Optimize, isMigrating ? tier : undefined),
        ),
        GrowFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Optimize, isMigrating ? tier : undefined),
        ),
        OptimizeFeatures.map(feature =>
          this.mapFeatureItem(feature, BillingPlanTier.Optimize, isMigrating ? tier : undefined),
        ),
      ],
    };
  }

  private mapFeatureItem(
    feature: FeatureRecord,
    planContext: BillingPlanTier,
    migrationFrom: BillingPlanTier | undefined,
  ): FeatureItemUI {
    if (!migrationFrom) return feature;

    let key: keyof FeatureRecord;
    if (migrationFrom === BillingPlanTier.Launch) {
      key = 'fromLaunch';
    } else {
      key = 'fromScale';
    }
    const onMigration = feature[key];
    if (onMigration === 'add') {
      return {
        ...feature,
        altIcon: 'add-circle-outline',
        isBold: true,
      };
    } else if (onMigration === 'remove' && planContext === BillingPlanTier.Start) {
      return {
        ...feature,
        altIcon: 'remove-circle',
        isBold: true,
      };
    }
    return feature;
  }

  private getAddonData(): AddonModelUI {
    return {
      [BillingPlanTier.Start]: {
        optional: StartAddons.filter(addon => addon.availability === 'optional'),
        included: StartAddons.filter(addon => addon.availability === 'included'),
      },
      [BillingPlanTier.Grow]: {
        optional: GrowAddons.filter(addon => addon.availability === 'optional'),
        included: GrowAddons.filter(addon => addon.availability === 'included'),
      },
      [BillingPlanTier.Optimize]: {
        optional: OptimizeAddons.filter(addon => addon.availability === 'optional'),
        included: OptimizeAddons.filter(addon => addon.availability === 'included'),
      },
    };
  }
}
